import { forwardRef, type ReactNode } from "react";
import { cn } from "~/lib/cn";

interface TextareaProps extends React.ComponentProps<"textarea"> {
  label?: string;
  /** Renders next to the label (e.g. info button) — parity with Input/Select. */
  labelAside?: ReactNode;
  error?: string;
  /**
   * Translated word for the optional tag (e.g. "opcional"), shown next to the label.
   * Must be a meaningful word (not punctuation): the chip lives inside `<label>`, so
   * it becomes part of the textarea's accessible name announced by screen readers.
   */
  optional?: string;
  /**
   * `public` (T872 S8) — the October delivery's message field: the `Input` public variant's
   * sentence-case label over a white field, the error line under it. Any `aria-describedby`
   * the consumer passes (a hint, a limit) is kept beside the error's id. No `labelAside`.
   */
  variant?: "default" | "public";
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea(
    { label, labelAside, error, optional, className, id, disabled, variant = "default", "aria-describedby": describedBy, ...props },
    ref,
  ) {
    const textareaId = id ?? label?.toLowerCase().replace(/\s+/g, "-");
    const helpId = error ? `${textareaId}-error` : undefined;
    const optionalChip = optional ? <span className="opt">{' '}{optional}</span> : null;

    if (variant === "public") {
      return (
        <div className={cn("pub-field", error && "is-error")}>
          {label && (
            <label htmlFor={textareaId} className="pub-field-label">
              {label}
              {optional ? <span className="pub-field-optional"> ({optional})</span> : null}
            </label>
          )}
          <textarea
            ref={ref}
            id={textareaId}
            className={cn("pub-field-input pub-field-textarea", className)}
            aria-invalid={error ? true : undefined}
            aria-describedby={[describedBy, helpId].filter(Boolean).join(" ") || undefined}
            disabled={disabled}
            {...props}
          />
          {error && (
            <p id={helpId} className="pub-field-error" role="alert">
              {error}
            </p>
          )}
        </div>
      );
    }

    return (
      <div className="af-field">
        {label && !labelAside && (
          <label htmlFor={textareaId} className="af-label">
            <span>{label}</span>
            {optionalChip}
          </label>
        )}
        {label && labelAside && (
          <div className="flex items-start justify-between gap-2">
            <label htmlFor={textareaId} className="af-label">
              <span>{label}</span>
              {optionalChip}
            </label>
            {labelAside}
          </div>
        )}
        <div
          className={cn(
            "af-textarea-wrap",
            error && "is-error",
            disabled && "is-disabled",
          )}
        >
          <textarea
            ref={ref}
            id={textareaId}
            className={cn("af-textarea", className)}
            aria-invalid={error ? true : undefined}
            aria-describedby={[describedBy, helpId].filter(Boolean).join(" ") || undefined}
            disabled={disabled}
            {...props}
          />
        </div>
        {error && (
          <p id={helpId} className="af-helper is-error" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  },
);
